import StatusFormComponent from '../components/status-form.component';
import { StatusUpdate } from '../types/status.types';
import { useStatusGet, useStatusUpdate } from '../hooks/use-status.hook';
import { message } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import {
  extractCreateError,
  extractValidationError,
} from '../../../../../services/extract-api-error.service';
import { useEffect, useState, useCallback } from 'react';
import HeaderRow from '../../../../common/HeaderRow';
import { ValidationError } from '../../../../../types/api-response.types';
import { useTranslation } from 'react-i18next';
import ErrorFallback from '../../../../common/ErrorFallback';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { DefaultValues } from 'react-hook-form';
import { WithPermissionCheck } from '../../../../auth/WithPermissionCheck';
import PageTitle from '@components/common/PageTitle';

interface FormSubmitHandler {
  (data: StatusUpdate, resetForm: () => void): void;
}

const StatusUpdateContainer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [formDefaultValues, setFormDefaultValues] = useState<DefaultValues<StatusUpdate>>({});
  const { data, isLoading, error } = useStatusGet(id ?? '');
  const { mutateAsync: updateStatus, isPending } = useStatusUpdate();

  const extractFormDefaultValues = useCallback(async () => {
    if (!data?.data) return;
    const { data: statusData } = data;

    setFormDefaultValues({
      candidateStep: statusData.candidateStep ?? undefined,
      stageDescription: statusData.stageDescription ?? undefined,
      statusCode: statusData.statusCode ?? undefined,
      statusOutcome: statusData.statusOutcome ?? undefined,
      purpose: statusData.purpose ?? undefined,
      actionBy: statusData.actionBy ?? undefined,
      actionRequired: statusData.actionRequired ?? undefined,
    });
  }, [data]);

  useEffect(() => {
    let isMounted = true; // to prevent memory leaks
    extractFormDefaultValues();
    return () => {
      isMounted = false;
    };
  }, [extractFormDefaultValues]);

  const onFormSubmit: FormSubmitHandler = useCallback(
    async (data, resetForm) => {
      if (!id) return;
      // Reset validation errors on new submission
      setValidationErrors([]);
      try {
        await updateStatus({ id, data });
        message.success(t('crud.update.success', { entity: t('statuses.title') }));
        navigate(-1);
      } catch (error: any) {
        const errorFields = extractValidationError(error);
        setValidationErrors(errorFields);
        const errorMessage = extractCreateError(
          error,
          t('crud.update.error', { entity: t('statuses.title') })
        );
        message.error(errorMessage);
      }
    },
    [id, updateStatus, t, navigate]
  );

  if (!id) {
    return (
      <ErrorFallback
        details={t('common.errorLoadingData')}
        icon={<ExclamationCircleOutlined />}
        error={new Error(t('common.invalidIdProvided'))}
        resetErrorBoundary={() => {}}
      />
    );
  }

  if (error) {
    return (
      <>
        <PageTitle title={t('crud.update.title', { entity: t('statuses.title') })} />
        <HeaderRow
          title={t('crud.update.title', { entity: t('statuses.title') })}
          showBackButton={true}
        />
        <ErrorFallback
          details={t('common.errorLoadingData')}
          icon={<ExclamationCircleOutlined />}
          error={error}
          resetErrorBoundary={() => {}}
        />
      </>
    );
  }

  return (
    <>
      <PageTitle title={t('crud.update.title', { entity: t('statuses.title') })} />
      <HeaderRow
        title={t('crud.update.title', { entity: t('statuses.title') })}
        showBackButton={true}
      />
      <div className="row mt-3">
        <StatusFormComponent
          onFormSubmit={onFormSubmit}
          isLoading={isPending || isLoading}
          defaultValues={formDefaultValues}
          validationErrors={validationErrors}
        />
      </div>
    </>
  );
};

export default WithPermissionCheck(StatusUpdateContainer, 'status.update');
